'use client';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useJobSubscription, persistJobToSupabase, loadJobsFromSupabase } from '@/lib/hooks/useJobSubscription';

export type JobStatus =
  | 'queued' |'discovering' |'resolving' |'scanning' |'extracting' |'verifying' |'classifying' |'deduplicating' |'completed' |'failed' |'cancelled';

export interface JobStats {
  companies: number;
  domainsResolved: number;
  contactsFound: number;
  verified: number;
  noContact: number;
  duplicatesRemoved: number;
  total: number;
}

export interface BulkResult {
  id: string;
  company: string;
  domain: string;
  email: string | null;
  purpose: string | null;
  status: 'verified' | 'inferred' | 'unverified';
  confidence: number;
  source: string | null;
  rowStatus: 'found' | 'no-contact' | 'processing';
}

export interface TrackedJob {
  id: string;
  prompt: string;
  status: JobStatus;
  progress: number;
  currentStage: string;
  stats: JobStats;
  startedAt: number;
  completedAt?: number;
  results: BulkResult[];
}

interface JobContextValue {
  jobs: TrackedJob[];
  activeJobId: string | null;
  startJob: (params: {
    companies: string[];
    purposes: string[];
    maxResults: number;
    prompt?: string;
  }) => Promise<string>;
  reconnectJob: (jobId: string) => void;
  cancelJob: (jobId: string) => Promise<void>;
  stopAllJobs: () => Promise<void>;
  getJob: (jobId: string) => TrackedJob | undefined;
  clearActiveJob: () => void;
}

const JobContext = createContext<JobContextValue | null>(null);

const TERMINAL_STATUSES: JobStatus[] = ['completed', 'failed', 'cancelled'];

export function JobProvider({ children }: { children: React.ReactNode }) {
  const [jobs, setJobs] = useState<TrackedJob[]>([]);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const eventSourcesRef = useRef<Map<string, EventSource>>(new Map());
  const supabase = createClient();

  // Resolve authenticated user once
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data?.user?.id ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Load persisted jobs from Supabase on mount (once userId is known)
  useEffect(() => {
    if (!userId) return;
    loadJobsFromSupabase(supabase, userId).then((persisted) => {
      if (persisted.length === 0) return;
      setJobs((prev) => {
        const existingIds = new Set(prev.map((j) => j.id));
        const newOnes = persisted.filter((j) => !existingIds.has(j.id));
        return newOnes.length > 0 ? [...prev, ...newOnes] : prev;
      });
    });
  }, [userId]);

  const updateJob = useCallback((jobId: string, updates: Partial<TrackedJob>) => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.id === jobId);
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ...updates };
      return updated;
    });
  }, []);

  // Real-time Supabase subscription — syncs updates from other tabs/sessions
  const handleRealtimeUpdate = useCallback((job: TrackedJob) => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.id === job.id);
      if (idx === -1) return prev;
      const current = prev[idx];
      // Only apply if the incoming progress is newer or status changed
      if (job.progress >= current.progress || job.status !== current.status) {
        const updated = [...prev];
        updated[idx] = { ...current, ...job };
        return updated;
      }
      return prev;
    });
  }, []);

  const handleRealtimeInsert = useCallback((job: TrackedJob) => {
    setJobs((prev) => {
      const exists = prev.some((j) => j.id === job.id);
      if (exists) return prev;
      return [job, ...prev];
    });
  }, []);

  useJobSubscription({
    userId,
    onJobUpdate: handleRealtimeUpdate,
    onJobInsert: handleRealtimeInsert,
  });

  const connectSSE = useCallback(
    (jobId: string) => {
      if (eventSourcesRef.current.has(jobId)) return;

      const es = new EventSource(`/api/jobs/${jobId}/stream`);
      eventSourcesRef.current.set(jobId, es);

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'snapshot') {
            const updates: Partial<TrackedJob> = {
              status: data.status,
              progress: data.progress,
              currentStage: data.currentStage,
              stats: data.stats,
              results: data.results || [],
            };
            updateJob(jobId, updates);
            // Persist snapshot to Supabase for cross-tab sync
            if (userId) {
              setJobs((prev) => {
                const job = prev.find((j) => j.id === jobId);
                if (job) persistJobToSupabase(supabase, { ...job, ...updates }, userId);
                return prev;
              });
            }
          } else if (data.type === 'progress') {
            const updates: Partial<TrackedJob> = {
              status: data.status,
              progress: data.progress,
              currentStage: data.currentStage,
              stats: data.stats,
              // Stream partial results in real-time as each company completes
              ...(Array.isArray(data.results) && data.results.length > 0
                ? { results: data.results }
                : {}),
            };
            updateJob(jobId, updates);
            // Persist progress updates to Supabase (throttled by SSE rate)
            if (userId) {
              setJobs((prev) => {
                const job = prev.find((j) => j.id === jobId);
                if (job) persistJobToSupabase(supabase, { ...job, ...updates }, userId);
                return prev;
              });
            }
          } else if (data.type === 'completed') {
            const updates: Partial<TrackedJob> = {
              status: 'completed',
              progress: 100,
              currentStage: 'Completed',
              stats: data.stats,
              results: data.results || [],
              completedAt: Date.now(),
            };
            updateJob(jobId, updates);
            es.close();
            eventSourcesRef.current.delete(jobId);
            // Persist final state to Supabase
            if (userId) {
              setJobs((prev) => {
                const job = prev.find((j) => j.id === jobId);
                if (job) persistJobToSupabase(supabase, { ...job, ...updates }, userId);
                return prev;
              });
            }
            // Fire completion toast with result summary
            setJobs((prev) => {
              const job = prev.find((j) => j.id === jobId);
              if (job) {
                const completedJob = { ...job, ...updates };
                const stats = completedJob.stats;
                const parts: string[] = [];
                if (stats.contactsFound > 0) parts.push(`${stats.contactsFound} found`);
                if (stats.verified > 0) parts.push(`${stats.verified} verified`);
                if (stats.companies > 0) parts.push(`${stats.companies} companies`);
                toast.success('Bulk search complete', {
                  description: parts.join(' · ') || completedJob.prompt,
                  duration: 8000,
                  action: {
                    label: 'View results',
                    onClick: () => {
                      window.location.href = `/bulk-search?jobId=${jobId}`;
                    },
                  },
                });
              }
              return prev;
            });
          } else if (data.type === 'cancelled' || data.type === 'failed') {
            const updates: Partial<TrackedJob> = {
              status: data.status,
              currentStage: data.type === 'cancelled' ? 'Cancelled' : 'Failed',
            };
            updateJob(jobId, updates);
            es.close();
            eventSourcesRef.current.delete(jobId);
            if (userId) {
              setJobs((prev) => {
                const job = prev.find((j) => j.id === jobId);
                if (job) persistJobToSupabase(supabase, { ...job, ...updates }, userId);
                return prev;
              });
            }
          }
        } catch {
          // ignore parse errors
        }
      };

      es.onerror = () => {
        es.close();
        eventSourcesRef.current.delete(jobId);
        setJobs((prev) => {
          const job = prev.find((j) => j.id === jobId);
          if (job && !TERMINAL_STATUSES.includes(job.status)) {
            setTimeout(() => connectSSE(jobId), 3000);
          }
          return prev;
        });
      };
    },
    [updateJob, userId, supabase]
  );

  const startJob = useCallback(
    async (params: {
      companies: string[];
      purposes: string[];
      maxResults: number;
      prompt?: string;
    }): Promise<string> => {
      const res = await fetch('/api/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (!res.ok) throw new Error('Failed to create job');
      const { jobId } = await res.json();

      const newJob: TrackedJob = {
        id: jobId,
        prompt: params.prompt || `Bulk search for ${params.companies.length} companies`,
        status: 'queued',
        progress: 0,
        currentStage: 'Queued',
        stats: {
          total: params.companies.length,
          companies: 0,
          domainsResolved: 0,
          contactsFound: 0,
          verified: 0,
          noContact: 0,
          duplicatesRemoved: 0,
        },
        startedAt: Date.now(),
        results: [],
      };

      setJobs((prev) => [newJob, ...prev]);
      setActiveJobId(jobId);
      connectSSE(jobId);

      // Persist initial job state to Supabase for cross-tab visibility
      if (userId) {
        persistJobToSupabase(supabase, newJob, userId);
      }

      return jobId;
    },
    [connectSSE, userId, supabase]
  );

  const reconnectJob = useCallback(
    (jobId: string) => {
      setActiveJobId(jobId);
      fetch(`/api/jobs/${jobId}`)
        .then((r) => r.json())
        .then((job) => {
          setJobs((prev) => {
            const exists = prev.find((j) => j.id === jobId);
            if (exists) {
              return prev.map((j) =>
                j.id === jobId
                  ? {
                      ...j,
                      status: job.status,
                      progress: job.progress,
                      currentStage: job.currentStage,
                      stats: job.stats,
                      results: job.results || [],
                    }
                  : j
              );
            }
            return [
              {
                id: job.id,
                prompt: job.prompt,
                status: job.status,
                progress: job.progress,
                currentStage: job.currentStage,
                stats: job.stats,
                startedAt: job.startedAt,
                completedAt: job.completedAt,
                results: job.results || [],
              },
              ...prev,
            ];
          });
          if (!TERMINAL_STATUSES.includes(job.status)) {
            connectSSE(jobId);
          }
        })
        .catch(() => {});
    },
    [connectSSE]
  );

  const cancelJob = useCallback(async (jobId: string) => {
    await fetch(`/api/jobs/${jobId}`, { method: 'DELETE' });
    const updates: Partial<TrackedJob> = { status: 'cancelled', currentStage: 'Cancelled' };
    updateJob(jobId, updates);
    const es = eventSourcesRef.current.get(jobId);
    if (es) {
      es.close();
      eventSourcesRef.current.delete(jobId);
    }
    if (userId) {
      setJobs((prev) => {
        const job = prev.find((j) => j.id === jobId);
        if (job) persistJobToSupabase(supabase, { ...job, ...updates }, userId);
        return prev;
      });
    }
  }, [updateJob, userId, supabase]);

  const stopAllJobs = useCallback(async () => {
    const runningJobs = jobs.filter((j) => !TERMINAL_STATUSES.includes(j.status));
    await Promise.all(runningJobs.map((j) => cancelJob(j.id)));
    toast.info(`Stopped ${runningJobs.length} job${runningJobs.length !== 1 ? 's' : ''}`, {
      description: 'All running bulk search jobs have been cancelled.',
    });
  }, [jobs, cancelJob]);

  const getJob = useCallback(
    (jobId: string) => jobs.find((j) => j.id === jobId),
    [jobs]
  );

  const clearActiveJob = useCallback(() => setActiveJobId(null), []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      eventSourcesRef.current.forEach((es) => es.close());
    };
  }, []);

  return (
    <JobContext.Provider
      value={{ jobs, activeJobId, startJob, reconnectJob, cancelJob, stopAllJobs, getJob, clearActiveJob }}
    >
      {children}
    </JobContext.Provider>
  );
}

export function useJobs() {
  const ctx = useContext(JobContext);
  if (!ctx) throw new Error('useJobs must be used within JobProvider');
  return ctx;
}
